// ProposalItemsSettingsModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Local
import {
  DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS,
  normalizeProposalItemsDisplayPrefs,
} from 'nodetypes/proposal/view/proposalItemsDisplayPrefs';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderItemsSettingsModal.css';
import './ProposalItemsSettingsModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Switch from 'components/switch/Switch';

const ITEM_DETAIL_OPTIONS = [
  { key: 'showDescription', label: 'Show description' },
  { key: 'showCoverPhoto', label: 'Show images' },
  { key: 'showLineNumber', label: 'Show autonumber' },
];

const COLUMN_OPTIONS = [
  { key: 'sku', label: 'SKU' },
  { key: 'itemType', label: 'Type' },
  { key: 'category', label: 'Category' },
  { key: 'status', label: 'Status' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'cost', label: 'Cost' },
  { key: 'msrp', label: 'MSRP' },
  { key: 'markup', label: 'Markup' },
  { key: 'margin', label: 'Margin' },
  { key: 'room', label: 'Room' },
  { key: 'color', label: 'Color' },
  { key: 'dimensions', label: 'Dimensions' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'shippingMarkup', label: 'Shipping Markup' },
  { key: 'taxable', label: 'Taxable' },
  { key: 'website', label: 'Website' },
];

/**
 * ProposalItemsSettingsModal
 */
const ProposalItemsSettingsModal = React.memo(({
  isOpen,
  onClose,
  prefs = DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS,
  onSave = null,
}) => {
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState(() => normalizeProposalItemsDisplayPrefs(prefs));

  useEffect(() => {
    if (!isOpen) return;
    setDraft(normalizeProposalItemsDisplayPrefs(prefs));
  }, [isOpen, prefs]);

  const handleDetailToggle = (key, nextValue) => {
    setDraft((prev) => ({
      ...prev,
      [key]: Boolean(nextValue),
    }));
  };

  const handleColumnToggle = (key, nextValue) => {
    setDraft((prev) => ({
      ...prev,
      columns: {
        ...(prev.columns || {}),
        [key]: Boolean(nextValue),
      },
    }));
  };

  const handleSave = () => {
    if (typeof onSave === 'function') {
      onSave(normalizeProposalItemsDisplayPrefs(draft));
    }
    if (typeof onClose === 'function') onClose();
  };

  return (
    <Modal
      iconKey="discover_tune"
      title="Settings"
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={onClose}
    >
      <ModalForm>
        <div className="purchase-order-items-settings-modal-section">
          <div className="purchase-order-items-settings-modal-section-title">
            Item Details
          </div>
          <div className="purchase-order-items-settings-modal-options">
            {ITEM_DETAIL_OPTIONS.map((option) => (
              <div
                key={option.key}
                className="purchase-order-items-settings-modal-option-row"
              >
                <div className="purchase-order-items-settings-modal-option-label">
                  {option.label}
                </div>
                <Switch
                  checked={Boolean(draft[option.key])}
                  onChange={(nextValue) => handleDetailToggle(option.key, nextValue)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="purchase-order-items-settings-modal-section">
          <div className="purchase-order-items-settings-modal-section-title">
            Manage columns
          </div>
          <div className="purchase-order-items-settings-modal-options">
            {COLUMN_OPTIONS.map((option) => (
              <div
                key={option.key}
                className="purchase-order-items-settings-modal-option-row"
              >
                <div className="purchase-order-items-settings-modal-option-label">
                  {option.label}
                </div>
                <Switch
                  checked={Boolean(draft.columns?.[option.key])}
                  onChange={(nextValue) => handleColumnToggle(option.key, nextValue)}
                />
              </div>
            ))}
          </div>
        </div>
      </ModalForm>

      <ModalButtons>
        <Button
          iconKey="check"
          label="Save"
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
});

ProposalItemsSettingsModal.displayName = 'ProposalItemsSettingsModal';

export default ProposalItemsSettingsModal;
